import { rtkApi } from '@/apis/rtkApi';
import type { LoanAdminInsights } from '@/types/LoanApplicationType';
import type { User } from '@/types/UserType';

type ApiWrap<T> = { success: boolean; data: T };

export interface AdminProfileLoanFile {
  _id: string;
  flowProfile: string;
  productType: string;
  status: string;
  underwritingMilestone?: string;
  address: string;
  loanAmount: number | null;
  assignedToAdminId: string | null;
  processorAdminId: string | null;
  createdAt: string;
  updatedAt: string;
  insights: LoanAdminInsights;
}

export interface AdminProfileContract {
  _id: string;
  contractId: string;
  side: 'buyer' | 'seller' | 'agent';
  address: string;
  status: string;
  isRatified: boolean;
  price: number | null;
  offerGoodUntil: string | null;
  closingDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminProfileMessage {
  _id: string;
  subject: string;
  templateKey: string;
  status: string;
  sentAt: string;
  sentBy: string | null;
  body: string | null;
  applicationId: string | null;
}

export interface AdminNote {
  _id: string;
  body: string;
  authorName?: string;
  createdAt: string;
}

export interface AdminTimelineEvent {
  at: string;
  kind:
    | 'account'
    | 'property'
    | 'contract'
    | 'loan'
    | 'document'
    | 'message'
    | 'note';
  title: string;
  detail?: string;
  actor?: string;
  applicationId?: string;
  contractId?: string;
  productId?: string;
}

export interface AdminProfileDocument {
  _id: string;
  source: 'loan_application' | 'contract';
  title: string;
  fileName: string;
  size: number;
  uploadedAt: string;
  context: string;
  downloadPath: string;
  downloadCount: number;
}

export interface AdminUserOverview {
  user: User & {
    displayName: string;
    createdAt?: string;
    lastLoginAt?: string | null;
  };
  pipeline: { stage: string; stageLabel: string; flowType: string };
  properties: {
    _id: string;
    productId?: string;
    address: string;
    price: number | null;
    transactionMode: 'purchase' | 'refinance';
    createdAt: string;
    updatedAt: string;
  }[];
  contracts: AdminProfileContract[];
  loanFiles: AdminProfileLoanFile[];
  documents: AdminProfileDocument[];
  messages: AdminProfileMessage[];
  notes: AdminNote[];
  timeline: AdminTimelineEvent[];
}

export interface AdminSearchResults {
  users: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    role: string;
  }[];
  properties: {
    _id: string;
    address: string;
    transactionMode: string;
    price: number | null;
    ownerId: string | null;
    ownerName: string;
  }[];
  contracts: (AdminProfileContract & {
    buyerId: string | null;
    buyerName: string;
  })[];
  loanFiles: {
    _id: string;
    flowProfile: string;
    status: string;
    address: string;
    applicantId: string | null;
    applicantName: string;
  }[];
}

export interface AdminStaffMember {
  _id: string;
  name: string;
  email: string;
}

export interface ContractParty {
  id: string | null;
  name: string;
  email: string;
}

export interface ContractParties {
  buyer: ContractParty;
  seller: ContractParty;
  agent: ContractParty;
}

export interface AdminClauseOverride {
  _id: string;
  clauseId: string;
  clauseName: string;
  action: 'reopen' | 'agree';
  value: unknown;
  reason: string;
  adminName: string;
  at: string;
}

export interface ContractAdminView {
  parties: ContractParties;
  offerGoodUntil: string | null;
  /** "2026-10-20" when the written-acceptance deadline has passed. */
  acceptanceDeadlinePassed: string | null;
  adminClauseOverrides: AdminClauseOverride[];
}

export const adminWorkspaceApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminUserOverview: builder.query<AdminUserOverview, string>({
      query: (userId) => ({ url: `/admin/user/${userId}/overview` }),
      transformResponse: (r: ApiWrap<AdminUserOverview>) => r.data,
      providesTags: (_r, _e, userId) => [
        { type: 'Admin User', id: `overview-${userId}` },
      ],
    }),
    addAdminNote: builder.mutation<AdminNote, { userId: string; body: string }>(
      {
        query: ({ userId, body }) => ({
          url: `/admin/user/${userId}/notes`,
          method: 'POST',
          data: { body },
        }),
        transformResponse: (r: ApiWrap<AdminNote>) => r.data,
        invalidatesTags: (_r, _e, { userId }) => [
          { type: 'Admin User', id: `overview-${userId}` },
        ],
      }
    ),
    deleteAdminNote: builder.mutation<void, { userId: string; noteId: string }>(
      {
        query: ({ userId, noteId }) => ({
          url: `/admin/user/${userId}/notes/${noteId}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_r, _e, { userId }) => [
          { type: 'Admin User', id: `overview-${userId}` },
        ],
      }
    ),
    sendAdminMessage: builder.mutation<
      { status: string; subject: string },
      {
        userId: string;
        subject: string;
        body: string;
        applicationId?: string | null;
      }
    >({
      query: ({ userId, ...data }) => ({
        url: `/admin/user/${userId}/message`,
        method: 'POST',
        data,
      }),
      transformResponse: (r: ApiWrap<{ status: string; subject: string }>) =>
        r.data,
      invalidatesTags: (_r, _e, { userId, applicationId }) => [
        { type: 'Admin User', id: `overview-${userId}` },
        ...(applicationId
          ? [{ type: 'LoanApplication' as const, id: applicationId }]
          : []),
      ],
    }),
    adminSearch: builder.query<AdminSearchResults, string>({
      query: (q) => ({ url: `/admin/search`, params: { q } }),
      transformResponse: (r: ApiWrap<AdminSearchResults>) => r.data,
    }),
    getAdminStaff: builder.query<AdminStaffMember[], void>({
      query: () => ({ url: `/admin/loan-applications/staff` }),
      transformResponse: (r: ApiWrap<AdminStaffMember[]>) => r.data ?? [],
    }),
    adminBulkLoanAction: builder.mutation<
      {
        action: string;
        results: { id: string; status: string; reason?: string }[];
      },
      {
        ids: string[];
        action: 'assign' | 'remind' | 'request_documents';
        assignedToAdminId?: string | null;
        processorAdminId?: string | null;
      }
    >({
      query: (data) => ({
        url: `/admin/loan-applications/bulk`,
        method: 'POST',
        data,
      }),
      transformResponse: (
        r: ApiWrap<{
          action: string;
          results: { id: string; status: string; reason?: string }[];
        }>
      ) => r.data,
      invalidatesTags: [
        { type: 'LoanApplication', id: 'ADMIN_LIST' },
        'Admin User',
      ],
    }),
    getContractAdminView: builder.query<ContractAdminView, string>({
      query: (contractId) => ({
        url: `/admin/contracts/${contractId}/admin-view`,
      }),
      transformResponse: (r: ApiWrap<ContractAdminView>) => r.data,
      providesTags: (_r, _e, id) => [{ type: 'Contract', id: `admin-${id}` }],
    }),
    extendContractDeadline: builder.mutation<
      { offerGoodUntil: string },
      { contractId: string; date: string; reason: string }
    >({
      query: ({ contractId, ...data }) => ({
        url: `/admin/contracts/${contractId}/deadline`,
        method: 'POST',
        data,
      }),
      transformResponse: (r: ApiWrap<{ offerGoodUntil: string }>) => r.data,
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'Contract', id: `admin-${contractId}` },
        { type: 'Contract', id: contractId },
        { type: 'Contract' },
        'ContractActivity',
      ],
    }),
    overrideContractClause: builder.mutation<
      AdminClauseOverride,
      {
        contractId: string;
        clauseId: string;
        clauseName: string;
        action: 'reopen' | 'agree';
        value?: unknown;
        reason: string;
      }
    >({
      query: ({ contractId, clauseId, ...data }) => ({
        url: `/admin/contracts/${contractId}/clauses/${encodeURIComponent(
          clauseId
        )}/override`,
        method: 'POST',
        data,
      }),
      transformResponse: (r: ApiWrap<AdminClauseOverride>) => r.data,
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'Contract', id: `admin-${contractId}` },
        'ContractActivity',
      ],
    }),
  }),
});

export const {
  useGetAdminUserOverviewQuery,
  useAddAdminNoteMutation,
  useDeleteAdminNoteMutation,
  useSendAdminMessageMutation,
  useAdminSearchQuery,
  useGetAdminStaffQuery,
  useAdminBulkLoanActionMutation,
  useGetContractAdminViewQuery,
  useExtendContractDeadlineMutation,
  useOverrideContractClauseMutation,
} = adminWorkspaceApi;
